/**
 * gridFreeze.ts — helpers for EditableGrid's opt-in frozen columns / sticky
 * header / hover scroll arrows (BUG-2026-0159).
 *
 * Sticky cells sit above scrolled content, so they need OPAQUE backgrounds.
 * Row tints in the grid are alpha() washes over the Paper background; painting
 * them directly on a sticky cell would let the scrolled-under content bleed
 * through. `layeredBg` reproduces the exact same visual by stacking the tint
 * as a background-image over a solid paper background-color.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { GridColumn } from './EditableGrid';

/** Width of the leading checkbox gutter (matches the header cell's width: 42). */
export const CHECKBOX_COL_WIDTH = 42;

/** Fallback width for a frozen column that didn't declare one. */
const DEFAULT_FROZEN_WIDTH = 100;

export const frozenColWidth = (col: GridColumn): number => col.width ?? DEFAULT_FROZEN_WIDTH;

/**
 * Sticky `left` offset for each of the first `freezeCount` data columns.
 * Entry i = checkbox gutter + widths of frozen columns 0..i-1.
 */
export const computeFrozenOffsets = (columns: GridColumn[], freezeCount: number): number[] => {
  const offsets: number[] = [];
  let left = CHECKBOX_COL_WIDTH;
  for (let i = 0; i < Math.min(freezeCount, columns.length); i += 1) {
    offsets.push(left);
    left += frozenColWidth(columns[i]);
  }
  return offsets;
};

/** Total pixel width of the frozen pane (checkbox gutter included). */
export const computeFrozenPaneWidth = (columns: GridColumn[], freezeCount: number): number =>
  columns.slice(0, freezeCount).reduce((sum, col) => sum + frozenColWidth(col), CHECKBOX_COL_WIDTH);

/**
 * Measures the REAL header-cell widths for the sticky-left offsets.
 * tableLayout:auto sizes columns to their content (header label + sort arrow
 * + filter funnel can exceed the configured width), so computed offsets from
 * config drift — and clamping cells to the config width clips the filter
 * buttons (BUG-2026-0159 rework: funnels hidden under the next frozen cell /
 * spilling over unfrozen headers).
 *
 * Offsets are CUMULATIVE offsetWidth SUMS, never offsetLeft: on a currently
 * STUCK sticky cell, offsetLeft reports the displaced (rendered) position, so
 * a re-measure firing while the grid is horizontally scrolled would poison
 * the offsets and shove the frozen pane out of alignment. Widths are layout
 * sizes — sticky displacement never changes them, so measuring stays valid
 * mid-scroll. Returns null until the table exists; callers fall back to the
 * config estimate for the first paint.
 */
export const useMeasuredFrozenOffsets = (
  ref: RefObject<HTMLElement | null>,
  columns: GridColumn[],
  freezeCount: number,
  rowCount: number,
): { offsets: number[]; paneWidth: number } | null => {
  const [measured, setMeasured] = useState<{ offsets: number[]; paneWidth: number } | null>(null);
  useLayoutEffect(() => {
    if (freezeCount <= 0) {
      setMeasured(null);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const ths = el.querySelectorAll<HTMLTableCellElement>('thead th');
      if (ths.length < freezeCount + 1) return; // +1: checkbox gutter cell
      const offsets: number[] = [];
      let left = ths[0].offsetWidth; // checkbox gutter
      for (let i = 1; i <= freezeCount; i += 1) {
        offsets.push(left);
        left += ths[i].offsetWidth;
      }
      const paneWidth = left;
      setMeasured((prev) =>
        prev
        && prev.paneWidth === paneWidth
        && prev.offsets.length === offsets.length
        && prev.offsets.every((v, i) => v === offsets[i])
          ? prev
          : { offsets, paneWidth },
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, [ref, columns, freezeCount, rowCount]);
  return measured;
};

/** Opaque paper base + optional alpha tint layered on top (see file header). */
export const layeredBg = (theme: Theme, tint?: string) => ({
  backgroundColor: theme.palette.background.paper,
  ...(tint && { backgroundImage: `linear-gradient(${tint}, ${tint})` }),
});

/** Right-edge affordance on the LAST frozen column — classic frozen-pane cue. */
export const frozenEdgeSx = (theme: Theme) => ({
  borderRight: `1px solid ${alpha(theme.border.color, 0.9)}`,
  boxShadow: `4px 0 6px -2px ${alpha('#000', 0.1)}`,
});

/**
 * Caps the grid body to the viewport space remaining below it, so the
 * TableContainer becomes its own vertical scroller (a sticky header inside a
 * window-scrolled table would never engage — the container must scroll).
 * Re-measures on window resize and on layout shifts above the grid (e.g. the
 * schedule banner mounting async).
 */
export const useViewportFitMaxHeight = (
  enabled: boolean,
  ref: RefObject<HTMLElement | null>,
  bottomGutter = 24,
  minHeight = 280,
): number | undefined => {
  const [maxH, setMaxH] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (!enabled) {
      setMaxH(undefined);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    const compute = () => {
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      setMaxH(Math.max(minHeight, window.innerHeight - docTop - bottomGutter));
    };
    compute();
    window.addEventListener('resize', compute);
    // Anything above the grid growing/shrinking moves our top edge.
    const ro = new ResizeObserver(compute);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', compute);
      ro.disconnect();
    };
  }, [enabled, ref, bottomGutter, minHeight]);
  return maxH;
};

/**
 * Tracks whether the container can scroll horizontally in each direction and
 * exposes a viewport-relative pager for the hover arrows.
 */
export const useHorizontalScrollState = (
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
) => {
  const [state, setState] = useState({ canScrollLeft: false, canScrollRight: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const canScrollLeft = el.scrollLeft > 1;
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setState((prev) =>
      prev.canScrollLeft === canScrollLeft && prev.canScrollRight === canScrollRight
        ? prev
        : { canScrollLeft, canScrollRight },
    );
  }, [ref]);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const table = el.querySelector('table');
    if (table) ro.observe(table); // column show/hide changes the scrollWidth
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [enabled, ref, update]);

  const pageBy = useCallback(
    (dir: -1 | 1) => {
      const el = ref.current;
      if (!el) return;
      // Gentle step (~2-3 columns, not a full screen) so every column in
      // between stays reachable — a 75% page jumped nearly the whole scroll
      // range on the WO grid (BUG-2026-0159 follow-up).
      el.scrollBy({ left: dir * Math.max(180, Math.round(el.clientWidth * 0.3)), behavior: 'smooth' });
    },
    [ref],
  );

  return { ...state, pageBy };
};
